import type * as type from "@vTypes";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import useGradeConfig from "@hooks/useGradeConfig";
import { useQueryClient } from "@tanstack/react-query";
import { images } from "@img/imagesMap";
import type { NurseResponse, NurseWithRelation } from "@vTypes/nurse-type";
import type {
  ShiftType,
  GradeSavePayload,
  GradeShiftCounts,
  GradeData,
} from "@utils/grade-util";
import {
  MIN_ASSIGNABLE_GRADES_COUNT,
  transformGradeData,
} from "@utils/grade-util";
import {
  linkPreceptorRelations,
  unlinkPreceptorRelations,
} from "@utils/nurse-util";
import { useMutation } from "@tanstack/react-query";
import useThrottle from "@hooks/useThrottle";
import { ConfirmationDialog } from "@/components/modal/ConfirmationDialog";
import { NameInputDialog } from "@/components/modal/NameInputDialog";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import { useIsAdmin } from "@hooks/useAuth";
import httpClient from "@config/axios";
import { MSG } from "@constants/MSG.ts";
import { toast } from "@/components/toast/CustomToast";
import { GradeTransferTables } from "./GradeTransferTables";
import {
  GradeShiftConfigModal,
  type GradeShiftGridRowDisplay,
} from "./GradeShiftConfigModal";
import GradeManagementModal from "./GradeManagementModal";
import ActionButton from "@/components/ui/button/btn-action";
import useRosterConfig from "@hooks/useRosterConfig";
import type { GradeNamesMap } from "@vTypes/gradeConfig";
import GridModalHeader from "@/components/ui/modal/grid-modal-header";
import GridModalTitle from "@/components/ui/modal/grid-modal-title";

const ENABLE_SHIFT_CONFIG_MODAL_RENDER = false;

type Grade = {
  id: string;
  label: string;
  count: number;
};

type GradeBundleState = {
  nursesByGrade: Record<string, NurseWithRelation[]>;
  unassignedNurses: NurseWithRelation[];
};

type GradeBundleAction =
  | { type: "apply"; fn: (prev: GradeBundleState) => GradeBundleState }
  | {
      type: "move_nurses";
      nurseIds: string[];
      fromKey: string;
      toKey: string;
    };

function gradeBundleReducer(
  state: GradeBundleState,
  action: GradeBundleAction
): GradeBundleState {
  if (action.type === "apply") {
    return action.fn(state);
  }

  const { nurseIds, fromKey, toKey } = action;
  if (!nurseIds.length || fromKey === toKey) return state;

  const fromPool =
    fromKey === "unassigned"
      ? state.unassignedNurses
      : (state.nursesByGrade[fromKey] ?? []);

  const moving = fromPool.filter((n) => nurseIds.includes(n.nurse_id));
  if (!moving.length) return state;

  const restFrom = fromPool.filter((n) => !nurseIds.includes(n.nurse_id));

  const nursesByGrade = { ...state.nursesByGrade };
  let unassignedNurses = [...state.unassignedNurses];

  if (fromKey === "unassigned") {
    unassignedNurses = restFrom;
  } else {
    nursesByGrade[fromKey] = restFrom;
  }

  const destGradeNum =
    toKey === "unassigned" ? 0 : parseInt(toKey.replace("grade", ""), 10);

  const moved = moving.map((n) => ({
    ...n,
    grade: destGradeNum,
  }));

  if (toKey === "unassigned") {
    unassignedNurses = [...unassignedNurses, ...moved];
  } else {
    nursesByGrade[toKey] = [...(nursesByGrade[toKey] ?? []), ...moved];
  }

  return { nursesByGrade, unassignedNurses };
}

function buildInitialNursesByGrade(
  initialKeys: string[],
  nurseDataWithRelation: NurseWithRelation[] | undefined
): Record<string, NurseWithRelation[]> {
  const initial: Record<string, NurseWithRelation[]> = {};
  initialKeys.forEach((k) => {
    initial[k] = [];
  });

  nurseDataWithRelation?.forEach((nurse) => {
    const gId = nurse.grade ? `grade${nurse.grade}` : null;
    if (gId) {
      if (!initial[gId]) initial[gId] = [];
      initial[gId].push(nurse);
    }
  });
  return initial;
}

/** `GradeTabs` 등과 동일한 상한 — 등급 열 최대 개수 */
const MAX_ASSIGNABLE_GRADES = 11;
const FIXED_GRADE_SHIFT_COUNT = 1;
const ENABLE_SHIFT_COVERAGE_VALIDATION = false;

function gradeNumberFromId(gradeId: string) {
  return parseInt(gradeId.replace("grade", ""), 10);
}

function isCustomShiftGrade(gradeId: string) {
  return gradeNumberFromId(gradeId) === 1;
}

function buildGradeShiftCounts(
  shiftKeys: readonly ShiftType[],
  value: number
): GradeShiftCounts {
  return shiftKeys.reduce((acc, key) => {
    acc[key] = value;
    return acc;
  }, {} as GradeShiftCounts);
}

function normalizeGradeShiftSettings(
  source: Record<string, GradeShiftCounts>,
  gradeRows: Grade[],
  shiftKeys: readonly ShiftType[]
): Record<string, GradeShiftCounts> {
  return gradeRows
    .filter((grade) => grade.id !== "total")
    .reduce<Record<string, GradeShiftCounts>>((acc, grade) => {
      const current = source[grade.id] ?? buildGradeShiftCounts(shiftKeys, 0);
      const next = {} as GradeShiftCounts;

      shiftKeys.forEach((key) => {
        next[key] = isCustomShiftGrade(grade.id)
          ? Number(current[key]) || 0
          : FIXED_GRADE_SHIFT_COUNT;
      });

      acc[grade.id] = next;
      return acc;
    }, {});
}

function buildInitialUnassigned(
  nurseDataWithRelation: NurseWithRelation[] | undefined
): NurseWithRelation[] {
  return (
    nurseDataWithRelation?.filter((nurse) => {
      const gId = nurse.grade ? `grade${nurse.grade}` : null;
      return !gId;
    }) || []
  );
}

export type GradeSettingContentsHandle = {
  triggerSave: () => void;
  triggerSaveDirect: () => void;
};

type GradeSettingContentsProps = {
  onClose: () => void;
  selectedGroupId: string;
  nurseDatas?: NurseResponse[];
  /** 상단 패널 저장 버튼 비활성화 등 — `GradeSettingModal` 에서 연결 */
  onMutationPendingChange?: (pending: boolean) => void;
  /** 변경사항 존재 여부를 상위 모달로 전달 */
  onDirtyChange?: (dirty: boolean) => void;
  hideTitle?: boolean;
};

const GradeSettingContents = forwardRef<
  GradeSettingContentsHandle,
  GradeSettingContentsProps
>(function GradeSettingContents(
  {
    onClose,
    selectedGroupId,
    nurseDatas,
    onMutationPendingChange,
    onDirtyChange,
    hideTitle = false,
  },
  ref
) {
  const [isOpen, setOpen] = useState(false);
  const [isShiftConfigOpen, setShiftConfigOpen] = useState(false);
  const [nameRequest, setNameRequest] = useState<
    | { kind: "add"; initialValue: string }
    | { kind: "rename"; gradeId: string; initialValue: string }
    | null
  >(null);
  const [deleteGradeId, setDeleteGradeId] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const queryClient = useQueryClient();
  const throttle = useThrottle(1500);
  const isAdmin = useIsAdmin();

  const groupId = isAdmin ? selectedGroupId : "";

  const availableShifts = queryClient.getQueryData<type.res_shifts[]>([
    "SHIFT",
    selectedGroupId,
  ]);

  const { data: rosterConfig } = useRosterConfig();
  /** NurseSwapTables 등과 동일: 미정(undefined)이면 미드 노출 */
  const isUseMid = rosterConfig?.use_mid !== false;

  const { data: gradConfigData } = useGradeConfig({
    selectedGroupId: groupId,
  });

  const nurseDataWithRelation = useMemo(
    () => linkPreceptorRelations(nurseDatas ?? []),
    [nurseDatas]
  );

  const initialKeys = useMemo(() => {
    const configKeys = Object.keys(gradConfigData?.constraints || {});

    const nurseKeys =
      nurseDataWithRelation
        ?.map((n) => (n.grade ? `grade${n.grade}` : null))
        .filter((k): k is string => !!k) || [];

    const uniqueKeys = Array.from(new Set([...configKeys, ...nurseKeys]));

    if (uniqueKeys.length === 0) {
      const gn = gradConfigData?.grade_names;
      if (gn && typeof gn === "object" && Object.keys(gn).length > 0) {
        return Object.keys(gn)
          .sort((a, b) => Number(a) - Number(b))
          .map((k) => `grade${k}`);
      }
      return [];
    }

    return uniqueKeys.sort((a, b) => {
      const numA = parseInt(a.replace("grade", "") || "0", 10);
      const numB = parseInt(b.replace("grade", "") || "0", 10);
      return numA - numB;
    });
  }, [gradConfigData, nurseDataWithRelation]);

  const [shiftSettings, setShiftSettings] = useState<
    Record<string, GradeShiftCounts>
  >(() => {
    const settings = { ...(gradConfigData?.constraints || {}) } as Record<
      string,
      GradeShiftCounts
    >;
    initialKeys.forEach((key) => {
      if (!settings[key]) {
        settings[key] = { D: 0, E: 0, N: 0 };
      }
    });
    return settings;
  });

  const [grades, setGrades] = useState<Grade[]>(() => {
    return [
      { id: "total", label: "총 지정 인원", count: 0 },
      ...initialKeys.map((key) => ({
        id: key,
        label: `Grade ${key.replace("grade", "")}`,
        count: 0,
      })),
    ];
  });

  const [gradeNameMap, setGradeNameMap] = useState<GradeNamesMap>({});
  const [draftShiftSettings, setDraftShiftSettings] = useState<Record<
    string,
    GradeShiftCounts
  > | null>(null);
  const [draftGrades, setDraftGrades] = useState<Grade[] | null>(null);
  const [draftGradeNameMap, setDraftGradeNameMap] =
    useState<GradeNamesMap | null>(null);

  const gradeNamesSyncKey = JSON.stringify(gradConfigData?.grade_names ?? null);

  useEffect(() => {
    if (gradeNamesSyncKey === "null") {
      setGradeNameMap({});
      return;
    }
    try {
      const parsed = JSON.parse(gradeNamesSyncKey) as GradeNamesMap | null;
      if (parsed && typeof parsed === "object") {
        setGradeNameMap({ ...parsed });
      } else {
        setGradeNameMap({});
      }
    } catch {
      setGradeNameMap({});
    }
  }, [selectedGroupId, gradConfigData?.config_id, gradeNamesSyncKey]);

  const cloneShiftSettings = useCallback(
    (source: Record<string, GradeShiftCounts>) =>
      Object.fromEntries(
        Object.entries(source).map(([k, v]) => [k, { ...v }])
      ) as Record<string, GradeShiftCounts>,
    []
  );

  const [bundle, dispatchBundle] = useReducer(
    gradeBundleReducer,
    [initialKeys, nurseDataWithRelation] as const,
    ([keys, nurses]) => ({
      nursesByGrade: buildInitialNursesByGrade(keys, nurses),
      unassignedNurses: buildInitialUnassigned(nurses),
    })
  );
  const nursesByGrade = bundle.nursesByGrade;
  const unassignedNurses = bundle.unassignedNurses;

  const bundleHydrateKey = useMemo(
    () =>
      `${selectedGroupId}:${initialKeys.join(",")}:${nurseDataWithRelation
        .map((n) => `${n.nurse_id}:${n.grade ?? ""}`)
        .join("|")}`,
    [initialKeys, nurseDataWithRelation, selectedGroupId]
  );
  const hydratedBundleKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (isDirty || hydratedBundleKeyRef.current === bundleHydrateKey) return;
    hydratedBundleKeyRef.current = bundleHydrateKey;

    dispatchBundle({
      type: "apply",
      fn: () => ({
        nursesByGrade: buildInitialNursesByGrade(
          initialKeys,
          nurseDataWithRelation
        ),
        unassignedNurses: buildInitialUnassigned(nurseDataWithRelation),
      }),
    });

    setGrades([
      { id: "total", label: "총 지정 인원", count: 0 },
      ...initialKeys.map((key) => ({
        id: key,
        label: `Grade ${key.replace("grade", "")}`,
        count: 0,
      })),
    ]);
  }, [bundleHydrateKey, initialKeys, isDirty, nurseDataWithRelation]);

  const [highlightedNurseId, setHighlightedNurseId] = useState<string | null>(
    null
  );

  const [selectedLeftGradeId, setSelectedLeftGradeId] = useState<string | null>(
    "unassigned"
  );
  const [selectedRightGradeId, setSelectedRightGradeId] = useState<
    string | null
  >(null);

  const assignableGradeIds = useMemo(
    () => grades.filter((g) => g.id !== "total").map((g) => g.id),
    [grades]
  );

  useEffect(() => {
    if (assignableGradeIds.length === 0) {
      setSelectedRightGradeId(null);
      return;
    }
    setSelectedRightGradeId((prev) => {
      if (prev === "unassigned") return "unassigned";
      if (prev && assignableGradeIds.includes(prev)) return prev;
      return assignableGradeIds[0];
    });
  }, [assignableGradeIds]);

  useEffect(() => {
    if (
      selectedLeftGradeId !== null &&
      selectedLeftGradeId !== "unassigned" &&
      !assignableGradeIds.includes(selectedLeftGradeId)
    ) {
      setSelectedLeftGradeId("unassigned");
    }
  }, [assignableGradeIds, selectedLeftGradeId]);

  const prevGroupId = useRef(selectedGroupId);
  useEffect(() => {
    if (prevGroupId.current !== selectedGroupId) {
      onClose();
    }
    prevGroupId.current = selectedGroupId;
  }, [selectedGroupId, onClose]);

  useEffect(() => {
    setIsDirty(false);
  }, [selectedGroupId, gradConfigData?.config_id]);

  const displayGrades = useMemo(() => {
    const totalAssignedCount = Object.values(nursesByGrade).reduce(
      (sum, list) => sum + list.length,
      0
    );
    return grades.map((g) => {
      if (g.id === "total") return { ...g, count: totalAssignedCount };
      const n = g.id.replace("grade", "");
      const custom = gradeNameMap[n]?.trim();
      const label = custom && custom.length > 0 ? custom : `Grade ${n}`;
      return {
        ...g,
        label,
        count: nursesByGrade[g.id]?.length || 0,
      };
    });
  }, [grades, nursesByGrade, gradeNameMap]);

  const draftDisplayGrades = useMemo(() => {
    const sourceGrades = draftGrades ?? grades;
    const sourceNameMap = draftGradeNameMap ?? gradeNameMap;
    const totalAssignedCount = Object.values(nursesByGrade).reduce(
      (sum, list) => sum + list.length,
      0
    );
    return sourceGrades.map((g) => {
      if (g.id === "total") return { ...g, count: totalAssignedCount };
      const n = g.id.replace("grade", "");
      const custom = sourceNameMap[n]?.trim();
      const label = custom && custom.length > 0 ? custom : `Grade ${n}`;
      return {
        ...g,
        label,
        count: nursesByGrade[g.id]?.length || 0,
      };
    });
  }, [draftGradeNameMap, draftGrades, gradeNameMap, grades, nursesByGrade]);

  /** D/E/N(/M): 근무자 표와 동일하게 default_shift별 첫 행으로 shift_id·색 고정 */
  const constraintLetters = useMemo(
    (): readonly ShiftType[] =>
      isUseMid ? ["D", "E", "N", "M"] : ["D", "E", "N"],
    [isUseMid]
  );

  /** GET 직후 local state가 비어 있으면 위반/저장 검증이 어긋나므로 constraints 1회 병합 */
  const shiftSettingsHydratedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    shiftSettingsHydratedKeyRef.current = null;
  }, [selectedGroupId]);

  useEffect(() => {
    const incoming = gradConfigData?.constraints;
    if (!incoming || Object.keys(incoming).length === 0) return;

    const hydrateKey = `${selectedGroupId}:${
      gradConfigData?.config_id ?? "noid"
    }`;
    if (shiftSettingsHydratedKeyRef.current === hydrateKey) return;
    shiftSettingsHydratedKeyRef.current = hydrateKey;

    setShiftSettings((prev) => {
      const next = { ...prev };
      for (const gk of Object.keys(incoming)) {
        const row = incoming[gk];
        if (!row) continue;
        const base: GradeShiftCounts = isUseMid
          ? { D: 0, E: 0, N: 0, M: 0 }
          : { D: 0, E: 0, N: 0 };
        next[gk] = {
          ...base,
          ...(prev[gk] ?? {}),
          ...row,
        };
        if (isUseMid && next[gk].M === undefined) {
          next[gk] = { ...next[gk], M: Number(row.M) || 0 };
        }
      }
      return next;
    });
  }, [gradConfigData, selectedGroupId, isUseMid]);

  const shiftDisplayInfo = useMemo((): GradeShiftGridRowDisplay[] => {
    const FALLBACK_COLOR: Partial<Record<ShiftType, string>> = {
      D: "#59DDB7",
      E: "#72BFFF",
      N: "#BAB0F0",
      M: "#C4B5FD",
    };
    /** default_shift별 첫 시프트 행 — shift_id 미제공(API 보강 예정) 시 name → 문자(D/E/N/M) 순 폴백 */
    const labelByDefault = new Map<string, string>();
    const colorByDefault = new Map<string, string>();

    (availableShifts ?? []).forEach((s) => {
      const key = s.default_shift;
      if (!key || labelByDefault.has(key)) return;

      const sid = typeof s.shift_id === "string" ? s.shift_id.trim() : "";
      const nm = typeof s.name === "string" ? s.name.trim() : "";
      const label = sid !== "" ? sid : nm !== "" ? nm : key;

      labelByDefault.set(key, label);
      colorByDefault.set(key, s.color ?? "");
    });

    return constraintLetters.map((letter) => ({
      constraintKey: letter,
      displayId: labelByDefault.get(letter) ?? letter,
      color: colorByDefault.get(letter) || FALLBACK_COLOR[letter] || "#9CA3AF",
    }));
  }, [availableShifts, constraintLetters]);

  useEffect(() => {
    if (!isUseMid) return;
    setShiftSettings((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const k of Object.keys(next)) {
        if (next[k].M === undefined) {
          next[k] = { ...next[k], M: 0 };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [isUseMid]);

  const totalStaffCount = useMemo(() => {
    const inGrades = Object.values(nursesByGrade).reduce(
      (sum, list) => sum + list.length,
      0
    );
    return inGrades + unassignedNurses.length;
  }, [nursesByGrade, unassignedNurses]);

  const modalGrades = draftGrades ?? grades;
  const normalizedShiftSettings = useMemo(
    () => normalizeGradeShiftSettings(shiftSettings, grades, constraintLetters),
    [constraintLetters, grades, shiftSettings]
  );
  const modalShiftSettings = useMemo(
    () =>
      normalizeGradeShiftSettings(
        draftShiftSettings ?? shiftSettings,
        modalGrades,
        constraintLetters
      ),
    [constraintLetters, draftShiftSettings, modalGrades, shiftSettings]
  );

  const gradeConfiguredTotal = useMemo(() => {
    return modalGrades
      .filter((g) => g.id !== "total")
      .reduce((sum, g) => {
        const s = modalShiftSettings[g.id];
        if (!s) return sum;
        return (
          sum +
          constraintLetters.reduce((acc, key) => acc + (Number(s[key]) || 0), 0)
        );
      }, 0);
  }, [modalGrades, modalShiftSettings, constraintLetters]);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (payload: GradeSavePayload) => {
      const [gradeResponse, nursesResponse] = await Promise.all([
        httpClient.post("/grade/config", payload, {
          params: isAdmin ? { group_id: selectedGroupId } : undefined,
        }),
        httpClient.post("/nurses/bulk-update", payload.nurses, {
          params: isAdmin ? { group_id: selectedGroupId } : undefined,
        }),
      ]);

      return { gradeResponse, nursesResponse };
    },
    onSuccess: () => {
      toast.success("Grade 설정이 완료되었습니다.");
      setIsDirty(false);
      setOpen(false);
      onClose();
      // useGradeConfig / 목록 — 사이드프로필 `useNurseDetail`: ["NURSES","detail",id] 는 list 키와 별도라 상세도 무효화
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.grade.config(selectedGroupId),
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.nurses.listScope(groupId),
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.nurses.detailScope(),
        refetchType: "all",
      });
    },
  });

  const handleConfirmSave = useCallback(() => {
    const assigned = Object.entries(nursesByGrade).flatMap(([gradeKey, list]) =>
      unlinkPreceptorRelations(list).map((nurse) => ({
        ...nurse,
        grade: parseInt(gradeKey.replace("grade", ""), 10),
      }))
    );

    const unassigned = unlinkPreceptorRelations(unassignedNurses).map(
      (nurse) => ({
        ...nurse,
        grade: 0,
      })
    );

    const payloadSettings: GradeData = !isUseMid
      ? Object.fromEntries(
          Object.entries(normalizedShiftSettings).map(([gk, v]) => {
            const row = { ...v };
            delete row.M;
            return [gk, row];
          })
        )
      : normalizedShiftSettings;

    const { constraints } = transformGradeData(payloadSettings);

    const grade_names: GradeNamesMap | null = (() => {
      const out: GradeNamesMap = {};
      grades
        .filter((g) => g.id !== "total")
        .forEach((g) => {
          const n = g.id.replace("grade", "");
          const nm = gradeNameMap[n]?.trim();
          if (nm) out[n] = nm;
        });
      return Object.keys(out).length > 0 ? out : null;
    })();

    const payload: GradeSavePayload = {
      null_grade_policy: gradConfigData?.null_grade_policy ?? "LOWEST",
      use_dynamic_scaling: gradConfigData?.use_dynamic_scaling ?? false,
      constraints,
      grade_names,
      use_mid: gradConfigData?.use_mid ?? isUseMid,
      default_shifts: gradConfigData?.default_shifts ?? null,
      nurses: [...assigned, ...unassigned],
    };

    return mutateAsync(payload);
  }, [
    nursesByGrade,
    unassignedNurses,
    isUseMid,
    normalizedShiftSettings,
    gradConfigData?.null_grade_policy,
    gradConfigData?.use_dynamic_scaling,
    gradConfigData?.use_mid,
    gradConfigData?.default_shifts,
    grades,
    gradeNameMap,
    mutateAsync,
  ]);

  const validateBeforeSave = useCallback(() => {
    if (!ENABLE_SHIFT_COVERAGE_VALIDATION) return true;
    return true;
  }, []);

  const handleSaveClick = useCallback(() => {
    throttle(() => {
      if (!validateBeforeSave()) return;
      setOpen(true);
    });
  }, [throttle, validateBeforeSave]);

  const handleSaveDirect = useCallback(() => {
    if (!validateBeforeSave()) return;
    void handleConfirmSave();
  }, [validateBeforeSave, handleConfirmSave]);

  useImperativeHandle(
    ref,
    () => ({
      triggerSave: handleSaveClick,
      triggerSaveDirect: handleSaveDirect,
    }),
    [handleSaveClick, handleSaveDirect]
  );

  useEffect(() => {
    onMutationPendingChange?.(isPending);
  }, [isPending, onMutationPendingChange]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const selectIdForSideprofile = (bool: boolean, nurseID: string) => {
    setHighlightedNurseId(bool ? null : nurseID);
  };

  const modalIsInvalidGrade = useCallback(() => false, []);

  const getInvalidGradeTooltip = useCallback(
    (): string | undefined => undefined,
    []
  );

  const openShiftConfigModal = () => {
    setDraftGrades(grades.map((g) => ({ ...g })));
    setDraftShiftSettings(cloneShiftSettings(normalizedShiftSettings));
    setDraftGradeNameMap({ ...gradeNameMap });
    setShiftConfigOpen(true);
  };

  const closeShiftConfigModal = () => {
    setShiftConfigOpen(false);
    setNameRequest(null);
    setDeleteGradeId(null);
    setDraftGrades(null);
    setDraftShiftSettings(null);
    setDraftGradeNameMap(null);
  };

  const confirmShiftConfigModal = () => {
    if (!draftGrades || !draftShiftSettings || !draftGradeNameMap) {
      closeShiftConfigModal();
      return;
    }

    const normalizedDraftShiftSettings = normalizeGradeShiftSettings(
      draftShiftSettings,
      draftGrades,
      constraintLetters
    );

    const gradesChanged =
      JSON.stringify(grades) !== JSON.stringify(draftGrades);
    const settingsChanged =
      JSON.stringify(normalizedShiftSettings) !==
      JSON.stringify(normalizedDraftShiftSettings);
    const namesChanged =
      JSON.stringify(gradeNameMap) !== JSON.stringify(draftGradeNameMap);

    const prevGradeIds = new Set(
      grades.filter((g) => g.id !== "total").map((g) => g.id)
    );
    const nextGradeIds = new Set(
      draftGrades.filter((g) => g.id !== "total").map((g) => g.id)
    );
    const deletedGradeIds = Array.from(prevGradeIds).filter(
      (id) => !nextGradeIds.has(id)
    );

    setGrades(draftGrades);
    setShiftSettings(normalizedDraftShiftSettings);
    setGradeNameMap(draftGradeNameMap);

    if (deletedGradeIds.length > 0) {
      dispatchBundle({
        type: "apply",
        fn: (state) => {
          const nursesByGrade = { ...state.nursesByGrade };
          const movedToUnassigned: NurseWithRelation[] = [];

          deletedGradeIds.forEach((gradeId) => {
            const from = nursesByGrade[gradeId] ?? [];
            if (from.length > 0) {
              movedToUnassigned.push(
                ...from.map((n) => ({
                  ...n,
                  grade: 0,
                }))
              );
            }
            delete nursesByGrade[gradeId];
          });

          nextGradeIds.forEach((gradeId) => {
            if (!nursesByGrade[gradeId]) {
              nursesByGrade[gradeId] = [];
            }
          });

          return {
            nursesByGrade,
            unassignedNurses: [...state.unassignedNurses, ...movedToUnassigned],
          };
        },
      });
    }

    const validGradeIds = new Set(
      draftGrades.filter((g) => g.id !== "total").map((g) => g.id)
    );
    setSelectedLeftGradeId((prev) => {
      if (prev === null || prev === "unassigned") return prev;
      return validGradeIds.has(prev) ? prev : "unassigned";
    });
    setSelectedRightGradeId((prev) => {
      if (prev === null || prev === "unassigned") return prev;
      return validGradeIds.has(prev) ? prev : null;
    });

    if (gradesChanged || settingsChanged || namesChanged) {
      setIsDirty(true);
    }
    closeShiftConfigModal();
  };

  const handleDraftAddGrade = () => {
    const sourceGrades = draftGrades ?? grades;
    const assignable = sourceGrades.filter((g) => g.id !== "total");
    if (assignable.length >= MAX_ASSIGNABLE_GRADES) {
      toast.error(
        `Grade는 최대 ${MAX_ASSIGNABLE_GRADES}개까지 추가할 수 있습니다.`
      );
      return;
    }

    const nums = assignable.map((g) => parseInt(g.id.replace("grade", ""), 10));
    const nextNum = nums.length > 0 ? Math.max(...nums) + 1 : 1;
    setNameRequest({ kind: "add", initialValue: `Grade ${nextNum}` });
  };

  const submitGradeName = (name: string) => {
    if (!nameRequest) return;
    const trimmed = name.trim();
    if (!trimmed) return;

    if (nameRequest.kind === "rename") {
      const n = nameRequest.gradeId.replace("grade", "");
      setDraftGradeNameMap((prev) => ({
        ...(prev ?? { ...gradeNameMap }),
        [n]: trimmed,
      }));
      setNameRequest(null);
      return;
    }

    const sourceGrades = draftGrades ?? grades;
    const sourceNameMap = draftGradeNameMap ?? gradeNameMap;
    const nums = sourceGrades
      .filter((g) => g.id !== "total")
      .map((g) => parseInt(g.id.replace("grade", ""), 10));
    const nextNum = nums.length > 0 ? Math.max(...nums) + 1 : 1;
    const newId = `grade${nextNum}`;

    setDraftGrades((prev) => {
      const base = prev ?? sourceGrades;
      const total = base.find((g) => g.id === "total")!;
      const rest = base.filter((g) => g.id !== "total");
      const appended = [...rest, { id: newId, label: trimmed, count: 0 }].sort(
        (a, b) => {
          const na = parseInt(a.id.replace("grade", ""), 10);
          const nb = parseInt(b.id.replace("grade", ""), 10);
          return na - nb;
        }
      );
      return [total, ...appended];
    });

    setDraftGradeNameMap((prev) => ({
      ...(prev ?? sourceNameMap),
      [String(nextNum)]: trimmed,
    }));

    setDraftShiftSettings((prev) => ({
      ...(prev ?? cloneShiftSettings(shiftSettings)),
      [newId]: buildGradeShiftCounts(
        constraintLetters,
        isCustomShiftGrade(newId) ? 0 : FIXED_GRADE_SHIFT_COUNT
      ),
    }));
    setNameRequest(null);
  };

  const handleDraftRenameGrade = (gradeId: string, currentLabel: string) => {
    setNameRequest({ kind: "rename", gradeId, initialValue: currentLabel });
  };

  const handleDraftDeleteGrade = (gradeId: string) => {
    const sourceGrades = draftGrades ?? grades;
    const assignable = sourceGrades.filter((g) => g.id !== "total");
    if (assignable.length <= MIN_ASSIGNABLE_GRADES_COUNT) {
      toast.error(
        `Grade는 최소 ${MIN_ASSIGNABLE_GRADES_COUNT}개가 필요합니다.`
      );
      return;
    }
    const moving = nursesByGrade[gradeId] ?? [];
    if (moving.length > 0) {
      setDeleteGradeId(gradeId);
      return;
    }

    deleteGrade(gradeId);
  };

  const deleteGrade = (gradeId: string) => {
    const sourceGrades = draftGrades ?? grades;

    setDraftGrades((prev) =>
      (prev ?? sourceGrades).filter((g) => g.id !== gradeId)
    );
    setDraftShiftSettings((prev) => {
      const next = { ...(prev ?? cloneShiftSettings(shiftSettings)) };
      delete next[gradeId];
      return next;
    });
    const num = gradeId.replace("grade", "");
    setDraftGradeNameMap((prev) => {
      const copy = { ...(prev ?? { ...gradeNameMap }) };
      delete copy[num];
      return copy;
    });
    setDeleteGradeId(null);
  };

  const handleDraftMinStaffChange = (
    gradeId: string,
    shift: ShiftType,
    value: string
  ) => {
    if (!isCustomShiftGrade(gradeId)) return;

    const newCount =
      value === "" ? null : Math.max(0, parseInt(value, 10) || 0);

    setDraftShiftSettings((prev) => {
      const base = prev ?? cloneShiftSettings(shiftSettings);
      return {
        ...base,
        [gradeId]: {
          ...(base[gradeId] ??
            (isUseMid ? { D: 0, E: 0, N: 0, M: 0 } : { D: 0, E: 0, N: 0 })),
          [shift]: newCount,
        },
      };
    });
  };

  const handleMoveNurses = (
    nurseIds: string[],
    fromKey: string,
    toKey: string
  ) => {
    if (!nurseIds.length || fromKey === toKey) return;
    dispatchBundle({ type: "move_nurses", nurseIds, fromKey, toKey });
    setIsDirty(true);
  };

  return (
    <>
      <ConfirmationDialog
        open={isOpen}
        onClose={() => setOpen(false)}
        onConfirm={handleConfirmSave}
        isPending={isPending}
        pendingText={MSG.CONFIG_SETTING}
        heading="Grade 설정 저장"
        title="Grade 설정을 저장하시겠습니까?"
        confirmText="저장"
      />
      {/* [인원설정 제거] 기존 Shift 인원 설정 모달은 비활성화(렌더 안 함)하고
          추가/이름수정/삭제 전용 GradeManagementModal 로 대체. draft 라이프사이클
          (open=openShiftConfigModal / confirm=confirmShiftConfigModal / close=closeShiftConfigModal)과
          저장 endpoint 는 그대로 재사용. 복구하려면 아래 false → true. */}
      {ENABLE_SHIFT_CONFIG_MODAL_RENDER && (
        <GradeShiftConfigModal
          isOpen={isShiftConfigOpen}
          onClose={closeShiftConfigModal}
          onConfirm={confirmShiftConfigModal}
          displayGrades={draftDisplayGrades}
          shiftSettings={modalShiftSettings}
          shiftDisplayInfo={shiftDisplayInfo}
          totalStaffCount={totalStaffCount}
          gradeConfiguredTotal={gradeConfiguredTotal}
          unassignedCount={unassignedNurses.length}
          isInvalidGrade={modalIsInvalidGrade}
          onMinStaffChange={handleDraftMinStaffChange}
          onRenameGrade={handleDraftRenameGrade}
          onDeleteGrade={handleDraftDeleteGrade}
          onAddGrade={handleDraftAddGrade}
          addGradeDisabled={
            (draftGrades ?? grades).filter((g) => g.id !== "total").length >=
            MAX_ASSIGNABLE_GRADES
          }
        />
      )}
      <GradeManagementModal
        isOpen={isShiftConfigOpen}
        onClose={closeShiftConfigModal}
        onSave={confirmShiftConfigModal}
        grades={draftDisplayGrades}
        onRenameGrade={handleDraftRenameGrade}
        onDeleteGrade={handleDraftDeleteGrade}
        onAddGrade={handleDraftAddGrade}
        addGradeDisabled={
          (draftGrades ?? grades).filter((g) => g.id !== "total").length >=
          MAX_ASSIGNABLE_GRADES
        }
      />
      <NameInputDialog
        open={nameRequest !== null}
        title={nameRequest?.kind === "add" ? "Grade 추가" : "Grade 이름 수정"}
        initialValue={nameRequest?.initialValue}
        onClose={() => setNameRequest(null)}
        onSubmit={submitGradeName}
      />
      <ConfirmationDialog
        open={deleteGradeId !== null}
        onClose={() => setDeleteGradeId(null)}
        onConfirm={() => {
          if (deleteGradeId) deleteGrade(deleteGradeId);
        }}
        isPending={false}
        heading="Grade 삭제"
        title={`배정된 간호사 ${(deleteGradeId && nursesByGrade[deleteGradeId]?.length) || 0}명이 미지정으로 이동합니다. 삭제하시겠습니까?`}
        confirmText="삭제"
      />
      <div>
        {!hideTitle && <GridModalTitle title="Grade 설정" />}

        <div className="flex flex-col w-full  mx-auto px-10">
          <GridModalHeader>
            <p>*근무자 관리 페이지에서 근무자 개별 Grade 설정가능</p>

            <div className="flex justify-end min-w-0">
              {/* Grade 추가/이름수정/삭제 전용 (인원 설정 제외) — GradeManagementModal */}
              <ActionButton
                className="text-foreground"
                icon={
                  <img
                    src={images.icon_grade_setting_blue}
                    alt="grade manage"
                    className=" transition-all group-hover:[filter:brightness(0)_saturate(100%)_invert(27%)_sepia(98%)_saturate(2453%)_hue-rotate(200deg)_brightness(96%)_contrast(101%)]"
                  />
                }
                onClick={openShiftConfigModal}
              >
                Grade 관리
              </ActionButton>
            </div>
          </GridModalHeader>
          <div className="mb-5 scrollbar-float overflow-auto">
            <div className=" ">
              <GradeTransferTables
                gradeBuckets={displayGrades.filter((g) => g.id !== "total")}
                nursesByGrade={nursesByGrade}
                unassignedNurses={unassignedNurses}
                invalidGradeTooltip={getInvalidGradeTooltip}
                selectedLeftGradeId={selectedLeftGradeId}
                selectedRightGradeId={selectedRightGradeId}
                onLeftGradeChange={setSelectedLeftGradeId}
                onRightGradeChange={setSelectedRightGradeId}
                onMoveNurses={handleMoveNurses}
                selectedGroupId={selectedGroupId}
                activeNurseIdForSideProfile={highlightedNurseId}
                onNurseClick={(id) =>
                  selectIdForSideprofile(highlightedNurseId === id, id)
                }
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

export default GradeSettingContents;
